<template>
    <b-modal
      id="add-or-update"
      :visible="isAddOrUpdateActive"
      centered
      hide-footer
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      @hidden="resetForm" 
      @hide="resetForm"
      @change="(val) => $emit('update:is-add-or-update-active', val)"
    >
      <template #default="{ hide }">
        <b-overlay :show="loading" class="px-2">
          <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
            <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
              {{ isUpdate ? 'Update District' : 'Add New District' }}
            </h5>
  
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
  
          <validation-observer
            #default="{ handleSubmit }"
            ref="update_modal_form"
          >
            <b-form
              class="my-1"
              @submit.prevent="handleSubmit(onCreateOrUpdate)"
              @reset.prevent="resetForm"
            >
  
              <b-alert
                v-if="errorMessage"
                variant="danger"
                :show="true"
              >
                <div class="alert-body font-small-2">
                  <b-card-text class="mb-0">
                    {{ errorMessage }}
                  </b-card-text>
                </div>
              </b-alert>
  
        
              
              <validation-provider
                #default="validationContext"
                name="Title"
                rules="required"
              >
                <b-form-group
                  label="Title"
                  label-for="title"
                >
                  <template #label>
                    <span>Title<sup style="color: tomato">*</sup></span>
                  </template>
  
                  <b-form-input
                    id="title"
                    v-model="new_data.title"
                    autofocus
                    placeholder=""
                    :disabled="disableForm"
                    :state="getValidationState(validationContext)"
                  />
  
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Area -->
              <b-form-group label="Area" label-for="Area">
                <template #label>
                  <span>Area<span style="color: tomato">*</span></span>
                </template>

                <validation-provider
                  #default="{ errors }"
                  name="Area"
                  rules="required"
                >
                  <v-select
                    v-model="new_data.area_id"
                    :loading="loading"
                    :clearable="false"
                    :options="allOptions"
                    :reduce="(val) => val.value"
                    :searchable="true"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    @search="onSearchArea"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Region / RMC (read-only, derived from selected Area) -->
              <b-form-group v-if="new_data.area_id" label="Region / RMC" label-for="area_location_display">
                <b-form-input
                  id="area_location_display"
                  :value="selectedAreaLocation"
                  disabled
                />
              </b-form-group>

              <!-- Code (optional, seeded with the parent area's code) -->
              <b-form-group label="Code" label-for="code">
                <b-form-input
                  id="code"
                  v-model="new_data.code"
                  placeholder="Optional"
                  :disabled="disableForm"
                />
                <small v-if="parentCodePrefix" class="text-muted">
                  Area code <strong>{{ parentCodePrefix }}</strong> is used as the prefix &mdash; type this district's part after it.
                </small>
                <small v-else-if="new_data.area_id" class="text-muted">
                  The selected area has no code set, so there is no prefix to apply.
                </small>
              </b-form-group>

              <!-- mission zone -->
              <b-form-group label="Mission Zone?" label-for="mission_zone" class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  name="mission_zone"
                  rules=""
                >
                  <b-form-checkbox
                    id="mission_zone"
                    v-model="new_data.mission_zone"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

            <!-- is pwic -->
              <b-form-group label="Is PWIC?" label-for="is_pwic" class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  name="is_pwic"
                  rules=""
                >
                  <b-form-checkbox
                    id="is_pwic"
                    v-model="new_data.is_pwic"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
    
              <!-- Form Actions -->
              <div class="d-flex mt-2">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                  :disabled="disableForm"
                >
                  {{ isUpdate ? 'Update' : 'Add' }}
                </b-button>
  
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="button"
                  variant="outline-secondary"
                  @click="hide"
                >
                  Cancel
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-overlay>
      </template>
    </b-modal>
  </template>
  
  <script>
  import {
    BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
    BFormTextarea, BFormInvalidFeedback, BButton, BFormFile, BFormCheckbox,
    BAlert, BCardText, BModal, BMedia, BMediaBody, BMediaAside, BImg,
  } from 'bootstrap-vue'
  
  import { required } from '@validations'
  import { get, cloneDeep, omit } from 'lodash';
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  
  import vSelect from 'vue-select'
  import Ripple from 'vue-ripple-directive'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  import LocationCodePrefix from '@core/mixins/LocationCodePrefix'
  
  const rawMemberTypesData = {
    title: "",
    code: "",
    area_id: "",
    mission_zone: false,
    is_pwic: false,
  }
  
  export default {
    components: {
      BImg,
      BForm,
      BAlert,
      BModal,
      BMedia, 
      BButton,
      vSelect,
      BOverlay,
      BSidebar,
      BCardText,
      BFormFile,
      BFormGroup,
      BMediaBody, 
      BFormInput,
      BMediaAside,
      BFormCheckbox,
      BFormTextarea,
      BFormInvalidFeedback,
  
      // Form Validation
      ValidationProvider,
      ValidationObserver,
    },
    directives: {
      Ripple,
    },
    mixins: [LocationCodePrefix],
    model: {
      prop: 'isAddOrUpdateActive',
      event: 'update:is-add-or-update-active',
    },
    props: {
      isAddOrUpdateActive: {
        type: Boolean,
        required: true,
      },
      updateData: {
        type: Object,
        default: null,
        required: false
      },

    },
    data() {
      return {
        new_data: cloneDeep(rawMemberTypesData),
        loading: false,
        errorMessage: "",
        required,
        areaOptions: [],
      }
    },
    computed: {
      allOptions() {
        return [
          ...this.areaOptions,
          ...this.selectedOption
        ]
      },
      isUpdate(){
        return !!this.updateData
      },
      disableForm(){
        if (this.isUpdate){
          return !this.can("districts:CanUpdate")
        }
  
        return !this.can("districts:CanAddNew")
      },
      selectedOption() {
        if (!this.isUpdate) { return [] }
        const { area } = this.updateData
        if (area) {
          return [
            {
              label: `${area.title} (${this.areaLocationLabel(area)})`,
              value: area._id,
              location: this.areaLocationLabel(area),
              code: area.code || "",
            }
          ]
        }
        return []
      },
      selectedAreaLocation() {
        const selected = this.allOptions.find((option) => option.value === this.new_data.area_id)
        return get(selected, 'location', '')
      },
      parentCodePrefix() {
        const selected = this.allOptions.find((option) => option.value === this.new_data.area_id)
        return get(selected, 'code', '')
      },
    },
    watch: {
      updateData: {
        handler(updateData){
          if (updateData){
            this.new_data = {
              _id: updateData._id,
              title: updateData.title,
              code: updateData.code || "",
              mission_zone: updateData.mission_zone,
              area_id: updateData.area_id,
              is_pwic: updateData.is_pwic,
            }
          }
        },
        immediate: true,
        deep: true
      },
      parentCodePrefix: {
        handler(prefix) {
          this.applyCodePrefix(prefix)
        },
      },
    },
    methods: {
      areaLocationLabel(area){
        const regionTitle = get(area, 'region.title');
        const rmcTitle = get(area, 'country.rmc.title');

        return regionTitle || rmcTitle || 'No RMC Assigned';
      },
      async onCreateOrUpdate(){
        try {
          this.loading = true;
          this.errorMessage = "";
  
          const success = await this.$refs.update_modal_form.validate();
          if (!success){
            return;
          }
  
          const messages = {
            update: {
              title: "Confirm Area Update",
              text: "You are about to update details of a area.",
              icon: "warning"
            },
            new: {
              title: "Create Area",
              text: "You are about to create an area",
              icon: "info"
            }
          }
  
          /* eslint-disable no-nested-ternary */
          const message = this.isUpdate ? messages.update : messages.new;
          const { title, text, icon } = message;
  
          const result = await this.$swal({
            title,
            text,
            icon,
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });
          if (!result.isConfirmed) {
            return;
          }
  
          if (this.isUpdate){
            const id = this.new_data._id
            const update_data = omit(this.new_data, "_id");
            await this.useJwt().sharedService.updateGeoDistrict(id, update_data);
          } else {
            await this.useJwt().sharedService.createGeoDistrict(this.new_data);
          }
          this.$emit("refetchData");
          this.$emit('update:is-add-or-update-active', false);
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success!',
              icon: 'AlertQuestionIcon',
              variant: 'success',
              text: `Member ${this.isUpdate ? 'updated' : 'added'} successfully`
            },
          });
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.errorMessage = error_message;
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false
        }
      },
      async onSearchArea(search, loading) {
        try {
          loading(true);

          if (!search) {
            return;
          }
  
          const query = {
            limit: this.perPage,
            search,
            page: this.currentPage,
          }
  
          const request = await this.useJwt().sharedService.fetchGeoAreas(query);
          const { data } = request.data;

          this.areaOptions = data.map((item) => ({
            label: `${item.title} (${this.areaLocationLabel(item)})`,
            value: item._id,
            location: this.areaLocationLabel(item),
            code: item.code || "",
          }))
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          console.log("error_message", error_message);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          loading(false);
        }
      },
  
      resetForm() {
        this.$emit("resetUpdateData")
        this.resetCodePrefix();
        this.new_data = {};
      }
    }
  }
  </script>
  
  <style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  
  #add-or-update {
    .vs__dropdown-menu {
      max-height: 200px !important;
    }
  }
  </style>
